import { useState, useEffect } from "react";

import heTranslations from "../translations/he/global.json";
import arTranslations from "../translations/ar/global.json";
import enTranslations from "../translations/en/global.json";

export type Language = "he" | "ar" | "en";
export type TranslationKey = string;

const ALL_LANGUAGES: Array<{ code: Language; name: string; flag: string }> = [
  { code: "en", name: "English", flag: "" },
  { code: "he", name: "עברית", flag: "" },
  { code: "ar", name: "العربية", flag: "" },
];

const isLanguage = (value: string | null): value is Language =>
  value === "he" || value === "ar" || value === "en";

interface TranslationData {
  [key: string]: any;
}

class TranslationService {
  private currentLanguage: Language = "en";
  private translations: Record<Language, TranslationData> = {
    he: heTranslations,
    ar: arTranslations,
    en: enTranslations,
  };
  private settleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.loadLanguageFromStorage();
  }

  private loadLanguageFromStorage(): void {
    const savedLanguage = localStorage.getItem("app_language");
    if (isLanguage(savedLanguage)) {
      this.currentLanguage = savedLanguage;
    }
    this.applyDocumentLanguage();
  }

  private saveLanguageToStorage(language: Language): void {
    localStorage.setItem("app_language", language);
  }

  private applyDocumentLanguage(): void {
    document.documentElement.lang = this.currentLanguage;
    document.documentElement.dir = this.getDirection();
  }

  public getCurrentLanguage(): Language {
    return this.currentLanguage;
  }

  public setLanguage(language: Language): void {
    if (!isLanguage(language)) return;
    window.dispatchEvent(new CustomEvent("languageSwitching"));
    this.currentLanguage = language;
    this.saveLanguageToStorage(language);
    this.applyDocumentLanguage();
    window.dispatchEvent(
      new CustomEvent("languageChanged", { detail: language }),
    );
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent("languageSwitchSettled"));
      this.settleTimer = null;
    }, 320);
  }

  /**
   * Country 1 (and unknown): English, Hebrew, Arabic.
   * Country 2 (West Bank): English and Arabic only.
   */
  public getAvailableLanguages(
    countryID?: string | null,
  ): Array<{ code: Language; name: string; flag: string }> {
    if (String(countryID || "") === "2") {
      return ALL_LANGUAGES.filter((language) => language.code !== "he");
    }
    return ALL_LANGUAGES;
  }

  /** If the saved language isn't allowed for this country, fall back to English. */
  public clampToCountry(countryID?: string | null): void {
    const allowed = this.getAvailableLanguages(countryID).map((l) => l.code);
    if (!allowed.includes(this.currentLanguage)) {
      this.setLanguage("en");
    }
  }

  public translate(
    key: TranslationKey,
    params?: Record<string, string | number>,
  ): string {
    const keys = key.split(".");
    let value: any = this.translations[this.currentLanguage];

    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        const fallback = this.lookup(this.translations.en, keys);
        return typeof fallback === "string"
          ? this.interpolate(fallback, params)
          : key;
      }
    }

    if (typeof value !== "string") {
      return key;
    }

    return this.interpolate(value, params);
  }

  private lookup(source: TranslationData, keys: string[]): unknown {
    let value: any = source;
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }
    return value;
  }

  private interpolate(
    value: string,
    params?: Record<string, string | number>,
  ): string {
    if (!params) return value;
    return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
      return params[paramKey]?.toString() || match;
    });
  }

  public getDirection(): "rtl" | "ltr" {
    return this.currentLanguage === "en" ? "ltr" : "rtl";
  }

  public isRTL(): boolean {
    return this.getDirection() === "rtl";
  }
}

export const translationService = new TranslationService();

export const useTranslation = () => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(
    translationService.getCurrentLanguage(),
  );

  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent<Language>) => {
      setCurrentLanguage(event.detail);
    };

    window.addEventListener(
      "languageChanged",
      handleLanguageChange as EventListener,
    );

    return () => {
      window.removeEventListener(
        "languageChanged",
        handleLanguageChange as EventListener,
      );
    };
  }, []);

  const t = (
    key: TranslationKey,
    params?: Record<string, string | number>,
  ): string => {
    return translationService.translate(key, params);
  };

  const changeLanguage = (language: Language): void => {
    translationService.setLanguage(language);
  };

  const getAvailableLanguages = (countryID?: string | null) => {
    return translationService.getAvailableLanguages(countryID);
  };

  const getCurrentLanguage = (): Language => {
    return translationService.getCurrentLanguage();
  };

  const getDirection = (): "rtl" | "ltr" => {
    return translationService.getDirection();
  };

  const isRTL = (): boolean => {
    return translationService.isRTL();
  };

  return {
    t,
    changeLanguage,
    getAvailableLanguages,
    getCurrentLanguage,
    getDirection,
    isRTL,
    currentLanguage,
  };
};

export default translationService;
